import {
  Box,
  Stack,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import CustomButton from 'components/Button/CustomButton';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Webcam from 'react-webcam';
import { InfoBanner, LoaderCnt, VideoCamRecorderCnt } from './style';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NoCameraAccess from './noCameraAccess';

interface VideoConstraints {
  width: number;
  height: number;
  facingMode: string;
}

const MAX_RECORDING_TIME = 180; // 3 minutes in seconds
const VIDEO_MIME_TYPE = 'video/mp4'; // More efficient codec

const VideoCamRecorder = ({ uploadHandlerCallback }) => {
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<any>(null);
  const [capturing, setCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoader, setIsLoader] = useState(true);
  const [showSizeInfo, setShowSizeInfo] = useState(false);
  const [isCameraAccess, setIsCameraAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStreamReady, setIsStreamReady] = useState(false);

  const videoConstraints: VideoConstraints = useMemo(
    () => ({
      width: 1280,
      height: 720,
      facingMode: 'user',
    }),
    [],
  );

  const handleStartCaptureClick = useCallback(async () => {
    try {
      if (!webcamRef.current?.stream) {
        throw new Error('No camera stream available');
      }

      setRecordedChunks([]);
      setRecordedVideoUrl(null);
      setRecordingTime(0);
      setCapturing(true);
      setError(null);

      const stream = webcamRef.current.stream;
      const options = { mimeType: VIDEO_MIME_TYPE };

      if (!window.MediaRecorder.isTypeSupported(VIDEO_MIME_TYPE)) {
        throw new Error('Video codec not supported');
      }

      mediaRecorderRef.current = new window.MediaRecorder(stream, options);
      mediaRecorderRef.current.addEventListener(
        'dataavailable',
        handleDataAvailable,
      );
      mediaRecorderRef.current.start();

      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => {
          if (prevTime >= MAX_RECORDING_TIME) {
            if (timerRef.current) clearInterval(timerRef.current);
            handleStopCaptureClick();
            return MAX_RECORDING_TIME;
          }
          return prevTime + 1;
        });
      }, 1000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to start recording',
      );
      setCapturing(false);
    }
  }, [webcamRef]);

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => [...prev, data]);
    }
  }, []);

  const handleStopCaptureClick = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    setCapturing(false);
  }, []);

  const cleanupMediaResources = useCallback(() => {
    if (recordedVideoUrl) {
      URL.revokeObjectURL(recordedVideoUrl);
    }
    if (webcamRef.current?.stream) {
      webcamRef.current.stream.getTracks().forEach((track) => track.stop());
    }
  }, [recordedVideoUrl]);

  useEffect(() => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, { type: VIDEO_MIME_TYPE });
      const url = URL.createObjectURL(blob);
      setRecordedVideoUrl(url);
    }
  }, [recordedChunks]);

  const requestCameraAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the camera stream after getting permission
      stream.getTracks().forEach((track) => track.stop());
      setIsCameraAccess(true);
    } catch (error) {
      setIsCameraAccess(false);
    }
  };

  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'camera' as PermissionName,
        });

        if (permissionStatus.state === 'granted') {
          setIsCameraAccess(true);
        } else if (permissionStatus.state === 'denied') {
          setIsCameraAccess(false);
        } else {
          await requestCameraAccess();
        }

        permissionStatus.onchange = () => {
          setIsCameraAccess(permissionStatus.state === 'granted');
        };

        setIsLoader(false);
        setTimeout(() => setShowSizeInfo(true), 1000);
      } catch (err) {
        console.error('Permission API not supported or error occurred:', err);
        setIsLoader(false);
        setError('Failed to check camera permissions');
      }
    };

    checkCameraPermission();
  }, []);

  useEffect(() => {
    return () => {
      cleanupMediaResources();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [cleanupMediaResources]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const uploadToServer = useCallback(async () => {
    try {
      if (!recordedChunks.length) {
        throw new Error('No video recorded');
      }

      const blob = new Blob(recordedChunks, { type: VIDEO_MIME_TYPE });
      const file = new File([blob], 'recorded-video.mp4', {
        type: VIDEO_MIME_TYPE,
      });

      uploadHandlerCallback?.([file]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload video');
    }
  }, [recordedChunks]);

  if (isLoader) {
    return (
      <LoaderCnt>
        <CircularProgress />
      </LoaderCnt>
    );
  }

  if (!isCameraAccess) {
    return <NoCameraAccess />;
  }

  return (
    <VideoCamRecorderCnt>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!recordedVideoUrl && (
        <Webcam
          height={336}
          ref={webcamRef}
          width={600}
          videoConstraints={videoConstraints}
          audio={false}
          onUserMedia={() => setIsStreamReady(true)}
          onUserMediaError={(err) => {
            console.error('Webcam error:', err);
            setIsCameraAccess(false);
          }}
        />
      )}

      <Box
        sx={{
          width: '100%',
          paddingTop: !capturing && !recordedChunks.length ? '10px' : '20px',
        }}
      >
        {capturing && (
          <Box
            sx={{
              width: '100%',
              padding: '10px 16px 16px 16px',
              backgroundColor: 'rgba(235, 234, 255, 1)',
              borderRadius: '14px',
              marginBottom: '10px',
            }}
          >
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Typography variant="body2">
                Recording time: {formatTime(recordingTime)}
              </Typography>
              <Typography variant="body2">
                {formatTime(MAX_RECORDING_TIME)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(recordingTime / MAX_RECORDING_TIME) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(104, 104, 254, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'rgba(104, 104, 254, 1) !important',
                },
              }}
            />
          </Box>
        )}

        {showSizeInfo && !capturing && !recordedChunks.length && (
          <InfoBanner direction="row" alignItems="center" gap="7px">
            <InfoOutlinedIcon className="icon" />
            <Typography className="infoContent">
              Videos can be up to 3 minutes long.
            </Typography>
          </InfoBanner>
        )}

        {capturing ? (
          <CustomButton
            variant="contained"
            onClick={handleStopCaptureClick}
            className="stopBtn"
          >
            Stop Recording
          </CustomButton>
        ) : !recordedChunks.length && showSizeInfo ? (
          <CustomButton
            variant="contained"
            onClick={handleStartCaptureClick}
            disabled={!isStreamReady}
          >
            Start Recording
          </CustomButton>
        ) : null}

        {recordedVideoUrl && (
          <Stack direction="column" spacing={2} mt={2}>
            <video
              src={recordedVideoUrl}
              controls
              style={{ width: '100%', maxWidth: 600 }}
            />
            <Stack direction="row" gap="10px" justifyContent="center">
              <CustomButton
                variant="outlined"
                onClick={() => {
                  setRecordedVideoUrl(null);
                  setTimeout(() => {
                    handleStartCaptureClick();
                  }, 2000);
                }}
              >
                Record Again
              </CustomButton>
              <CustomButton variant="contained" onClick={uploadToServer}>
                Upload Video
              </CustomButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </VideoCamRecorderCnt>
  );
};

export default VideoCamRecorder;
